// src/dataCapture/index.js  →  dist/dataCapture.js  (global: DataCapture)
//
// Customer Data Capture — Admin upload + KPI tab (PR2 of 3).
//
// The owner's weekly sheet records, per store per week, how many customer
// bills were rung, and of those how many captured a Mobile number and/or an
// Email. This tab lets an admin upload that spreadsheet and shows a KPI grid
// of capture % per store over week / month / quarter / year.
//
// TWO metrics, always shown SEPARATELY (a locked owner decision — never a
// combined "contactable %"):
//   mobile% = Σmobiles / Σbills   over the period
//   email%  = Σemails  / Σbills   over the period
// The roll-up SUMS the raw counts across the period THEN divides — it NEVER
// averages the weekly %s (a busy week must weigh more than a quiet one).
//
// Backend (PR1, worker.js):
//   POST /api/admin/data-capture — body = array of
//        { store, week_start, bills, mobiles, emails, comment? }, upserts on
//        (store, week_start). requireAdmin.
//   GET  /api/admin/data-capture?from=YYYY-MM-DD&to=YYYY-MM-DD — raw weekly rows.
//        requireAdmin.
//
// Auth for both = the admin Bearer (sessionStorage 'elcAdminToken'), absolute
// Worker URL via DB.workerUrl() — a relative /api/* path 404s on Netlify.
//
// Free globals referenced as bare identifiers (lexical scope from admin.html):
//   - window.XLSX          (SheetJS, loaded via CDN in admin.html head)
//   - DB                   (db.js — const, so bare `DB`, not window.DB)
//   - mapAmanahStore       (src/uploads/match.js, window-bound) — store resolution
//   - window.escapeHtml / window.showToast  (admin.html inline core)

import { mapAmanahStore } from '../uploads/match.js';
import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function toast(msg, kind) {
  if (typeof window !== 'undefined' && window.showToast) window.showToast(msg, kind);
}

// ── DEFAULT RAG TARGETS ──────────────────────────────────────────────────────
// Hard-coded starting cut-offs. PR3 WILL MAKE THESE EDITABLE (per-tenant/store
// targets saved server-side); until then these are the owner's sensible defaults.
//   mobile%: green ≥ 25, amber ≥ 15, red < 15
//   email%:  green ≥ 10, amber ≥ 5,  red < 5
// Exported so the roll-up test + PR3 can reference the same numbers.
export const RAG_TARGETS = {
  mobile: { green: 25, amber: 15 },   // PR3 will make these editable
  email:  { green: 10, amber: 5 },    // PR3 will make these editable
};

// Score → band for a given metric ('mobile'|'email'). null pct → null (no data).
export function captureBand(metric, pct) {
  if (pct == null || !isFinite(Number(pct))) return null;
  const t = RAG_TARGETS[metric];
  if (!t) return null;
  const n = Number(pct);
  if (n >= t.green) return 'green';
  if (n >= t.amber) return 'amber';
  return 'red';
}
// Soft background + dark text per band (dark text on a pale fill — accessible).
export function bandStyle(band) {
  if (band === 'green') return { bg: 'var(--ok-bg)', fg: 'var(--ok-deep)' };
  if (band === 'amber') return { bg: 'var(--warn-bg)', fg: 'var(--warn-deep)' };
  if (band === 'red')   return { bg: 'var(--danger-bg)', fg: 'var(--danger-deep)' };
  return { bg: 'transparent', fg: 'var(--tiny)' };   // no data that period
}

// ── PERIOD ENGINE (client-side; mirrors src/worker/cx-timeline.js) ────────────
// Data-capture buckets its raw weekly GET rows CLIENT-SIDE. The key/start logic
// mirrors cx-timeline.js's periodKeyForDate/periodStartForDate for week/month/
// quarter, and ADDS a 'year' branch (owner asked for a calendar-year roll-up).
// The helpers are pure (date string in → key/start string out) and duplicated
// (not imported) because that module is worker-side; keeping a small client copy
// avoids coupling the admin bundle to the Worker. Exported for tests.
export const PERIODS = ['week', 'month', 'quarter', 'year'];

// How many period columns the grid shows, by period (readability cap):
// last 12 weeks / 12 months / 8 quarters / 5 years.
export function colCap(period) {
  return period === 'week' ? 12 : period === 'quarter' ? 8 : period === 'year' ? 5 : 12;
}

function dateOnly(value) {
  if (value == null) return null;
  const s = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}
function isoWeek(dateStr) {
  const parts = String(dateStr).split('-');
  const d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}
function isoWeekMonday(dateStr) {
  const parts = String(dateStr).split('-');
  const d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() || 7) - 1));
  return d.toISOString().slice(0, 10);
}

// Pure: bucket KEY (== display label) for a date + period. null if unparseable.
//   week → '2026-W27' | month → '2026-07' | quarter → '2026-Q3' | year → '2026'
export function periodKeyForDate(dateStr, period) {
  const day = dateOnly(dateStr);
  if (!day) return null;
  if (period === 'week') {
    const w = isoWeek(day);
    return w.year + '-W' + String(w.week).padStart(2, '0');
  }
  if (period === 'quarter') {
    const parts = day.split('-');
    const q = Math.floor((Number(parts[1]) - 1) / 3) + 1;
    return parts[0] + '-Q' + q;
  }
  if (period === 'year') return day.slice(0, 4);     // 'YYYY' — calendar year
  return day.slice(0, 7);                            // default: month 'YYYY-MM'
}
// Pure: inclusive START date (YYYY-MM-DD) of the bucket containing dateStr.
//   week → ISO-week Monday | month → 1st | quarter → quarter's 1st | year → Jan 1
export function periodStartForDate(dateStr, period) {
  const day = dateOnly(dateStr);
  if (!day) return null;
  if (period === 'week') return isoWeekMonday(day);
  const parts = day.split('-');
  if (period === 'quarter') {
    const q = Math.floor((Number(parts[1]) - 1) / 3);
    const startMonth = q * 3 + 1;
    return parts[0] + '-' + String(startMonth).padStart(2, '0') + '-01';
  }
  if (period === 'year') return parts[0] + '-01-01';   // calendar-year start
  return parts[0] + '-' + parts[1] + '-01';            // default: month
}

// Short column label from a bucket key.
//   '2026-W27' → 'W27' · '2026-07' → 'Jul' · '2026-Q3' → 'Q3' · '2026' → '2026'
export function colLabel(bucketLabel) {
  const s = String(bucketLabel || '');
  const wk = s.match(/^\d{4}-(W\d{1,2})$/);
  if (wk) return wk[1];
  const qt = s.match(/^\d{4}-(Q\d)$/);
  if (qt) return qt[1];
  const mo = s.match(/^(\d{4})-(\d{2})$/);
  if (mo) {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return names[Number(mo[2]) - 1] || s;
  }
  return s;   // year '2026' passes through
}

// ── THE ROLL-UP (pure, exported for tests) ────────────────────────────────────
// Given raw weekly rows [{ store, week_start, bills, mobiles, emails }] and a
// period, SUM bills/mobiles/emails per (store, period-bucket) THEN divide to get
// mobile%/email%. NEVER averages weekly %s — a low-traffic week must not weigh
// the same as a busy one.
//
// Returns:
//   { cols:   [{ label, colLabel, periodStart }],        // oldest → newest
//     allRow: { store:'All stores', all:true, cells:[…] },// company totals
//     rows:   [{ store, cells:[{ bills, mobiles, emails, mobilePct, emailPct }|null] }] }
// A store with no bills in a period → a null cell. mobilePct/emailPct are null
// when bills is 0 (can't divide). maxCols caps to the most-recent columns.
export function rollup(rows, period, maxCols) {
  const p = PERIODS.indexOf(period) !== -1 ? period : 'month';
  const list = Array.isArray(rows) ? rows : [];

  // Accumulate raw counts per bucket-key, and per (key, store).
  const bucketMeta = {};                 // key → periodStart
  const totalsByKey = {};                // key → { bills, mobiles, emails }
  const byKeyStore = {};                 // key → store → { bills, mobiles, emails }
  const storeSet = new Set();

  list.forEach((r) => {
    if (!r || !r.store) return;
    const key = periodKeyForDate(r.week_start, p);
    if (!key) return;                    // undated / unparseable → dropped (never bucketed)
    if (!bucketMeta[key]) bucketMeta[key] = periodStartForDate(r.week_start, p);
    const b = Number(r.bills) || 0;
    const m = Number(r.mobiles) || 0;
    const e = Number(r.emails) || 0;
    if (!totalsByKey[key]) totalsByKey[key] = { bills: 0, mobiles: 0, emails: 0 };
    totalsByKey[key].bills += b; totalsByKey[key].mobiles += m; totalsByKey[key].emails += e;
    if (!byKeyStore[key]) byKeyStore[key] = {};
    if (!byKeyStore[key][r.store]) byKeyStore[key][r.store] = { bills: 0, mobiles: 0, emails: 0 };
    const cell = byKeyStore[key][r.store];
    cell.bills += b; cell.mobiles += m; cell.emails += e;
    storeSet.add(r.store);
  });

  // Percentages, computed AFTER summing (SUM-then-divide). null when bills is 0.
  const pct = (num, den) => (den > 0 ? Math.round((num / den) * 1000) / 10 : null);   // 1 dp
  const withPct = (acc) => acc && ({
    bills: acc.bills, mobiles: acc.mobiles, emails: acc.emails,
    mobilePct: pct(acc.mobiles, acc.bills), emailPct: pct(acc.emails, acc.bills),
  });

  // Keys newest → oldest, keep the most-recent maxCols, then display oldest → newest.
  const allKeys = Object.keys(bucketMeta).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  const cap = Math.max(0, maxCols || allKeys.length);
  const keptNewestFirst = allKeys.slice(0, cap);
  const cols = keptNewestFirst.slice().reverse().map((label) => ({
    label, colLabel: colLabel(label), periodStart: bucketMeta[label],
  }));

  const cellFor = (label, store) => {
    const acc = byKeyStore[label] && byKeyStore[label][store];
    return (acc && acc.bills > 0) ? withPct(acc) : null;
  };

  const rows2 = [...storeSet].map((store) => ({
    store,
    cells: cols.map((c) => cellFor(c.label, store)),
  }));
  // Sort worst recent mobile% first (nulls sink). "Recent" = rightmost column
  // with data for that store.
  const recentMobileOf = (cells) => {
    for (let i = cells.length - 1; i >= 0; i--) {
      if (cells[i] && cells[i].mobilePct != null) return cells[i].mobilePct;
    }
    return null;
  };
  rows2.forEach((r) => { r._recent = recentMobileOf(r.cells); });
  rows2.sort((a, b) => {
    if (a._recent == null && b._recent == null) return String(a.store).localeCompare(String(b.store));
    if (a._recent == null) return 1;
    if (b._recent == null) return -1;
    return a._recent - b._recent;
  });
  rows2.forEach((r) => { delete r._recent; });

  const allCells = cols.map((c) => {
    const acc = totalsByKey[c.label];
    return (acc && acc.bills > 0) ? withPct(acc) : null;
  });

  return { cols, allRow: { store: 'All stores', all: true, cells: allCells }, rows: rows2 };
}

// ── SPREADSHEET PARSER (the transposed matrix) ────────────────────────────────
// The owner's weekly sheet is ONE transposed matrix:
//   Row 1 (AOA index 0): store names, each occupying a 5-COLUMN block starting
//     at columns B, G, L, Q, V … (1-based every-5th → 0-based indices 1, 6, 11, …).
//     Names look like 'ELC - DUBAI - DUBAI MALL'.
//   Row 2 (AOA index 1): sub-headers repeating per block:
//     [ No. of bills | customer | Mobile | Email | % ]  → within a store block the
//     column order is [bills, customer(IGNORE), Mobile, Email, %(IGNORE — recomputed)].
//   Column A (AOA col 0), rows 3..N (AOA index 2+): week labels as TEXT, e.g.
//     '05-11 Jan 2026', '26 Jan - 01 Feb 2026', '23 Feb - 01 Mar 2026'.
//
// So each (week-row × store-block) yields { store, week_start, bills, mobiles, emails }.
// mobiles = the Mobile column, emails = the Email column. The customer column and
// the % column are ignored (% is recomputed on read).

// Store-block starting columns (0-based): 1, 6, 11, 16, … up to the row width.
// Exported for tests.
export function storeBlockCols(headerRowLen) {
  const cols = [];
  for (let c = 1; c < headerRowLen; c += 5) cols.push(c);
  return cols;
}

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

// Pure: parse a week label as TEXT → week_start 'YYYY-MM-DD' (the FIRST day of the
// label), or null if unparseable. The rule (from the owner's real file):
//   - first NUMBER in the label = start day
//   - first MONTH NAME in the label = start month
//   - the 4-digit YEAR = the year
// For a cross-year Dec→Jan label the START uses the EARLIER year (label carries the
// later year, but the range began in December of the prior year). Examples:
//   '05-11 Jan 2026'       → 2026-01-05
//   '26 Jan - 01 Feb 2026' → 2026-01-26
//   '23 Feb - 01 Mar 2026' → 2026-02-23
//   '30 Mar - 05 Apr 2026' → 2026-03-30
//   '28 Dec - 03 Jan 2026' → 2025-12-28  (cross-year: start month is Dec, later part is Jan)
// Exported for tests.
export function parseWeekStart(label) {
  const s = String(label == null ? '' : label).trim();
  if (!s) return null;
  const yearMatch = s.match(/(\d{4})/);
  if (!yearMatch) return null;
  const year = Number(yearMatch[1]);
  // First month NAME in the label = the start month.
  const monthMatch = s.toLowerCase().match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/);
  if (!monthMatch) return null;
  const startMonth = MONTHS[monthMatch[1]];
  // First NUMBER (that isn't the 4-digit year) = the start day. Strip the year
  // token first so a label like '05-11 Jan 2026' reads 05, not 2026.
  const dayMatch = s.replace(yearMatch[1], '').match(/(\d{1,2})/);
  if (!dayMatch) return null;
  const startDay = Number(dayMatch[1]);
  if (!(startDay >= 1 && startDay <= 31) || !startMonth) return null;
  // Cross-year Dec→Jan: the label carries the later (Jan) year, but the range
  // started in December of the PRIOR year. Detect: start month is December AND a
  // Jan also appears later in the label → the start belongs to year-1.
  let startYear = year;
  const allMonths = (s.toLowerCase().match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/g) || []);
  if (startMonth === 12 && allMonths.length > 1 && allMonths[allMonths.length - 1] === 'jan') {
    startYear = year - 1;
  }
  const mm = String(startMonth).padStart(2, '0');
  const dd = String(startDay).padStart(2, '0');
  return startYear + '-' + mm + '-' + dd;
}

function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
}

// v125.30 (Matt's amended template): the owner's sheet carries ROLL-UP columns —
// per-area-manager totals (one column per area manager, headed with a first name) and a grand total
// ("Total UAE + Qatar"). Those are deliberate in the sheet but must not be
// saved (the KPI grid recalculates every roll-up from the store rows), and they
// must not read as ERRORS either — 108 orange warnings made a perfectly good
// upload look broken. An UNRESOLVED header is a roll-up when it says "total" or
// is a bare first name (one word, no "ELC"/" - " store shape). A misspelled real
// store (multi-word) still gets the genuine warning. Exported for tests.
export function isRollupHeader(raw) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return false;
  if (/\btotal\b/i.test(s)) return true;   // word-bounded: "Total UAE + Qatar" yes, "TOTALLY…" no
  return !/ - /.test(s) && !/^ELC/i.test(s) && s.split(/\s+/).length === 1;
}

// Parse the AOA (array-of-arrays, from sheet_to_json {header:1}) into candidate
// rows. Each result: { rawStore, resolvedStore, week_start, rawWeek, bills,
// mobiles, emails, save, reason, rollup }. `save` is true only when the store
// resolved to a live store AND the week label parsed AND there's at least one
// non-null count. Unresolved store / unparseable week → save:false with a reason
// (SURFACED, not dropped); a roll-up column is save:false + rollup:true (benign).
// Blank store blocks + blank week rows are skipped entirely.
export function parseSheet(aoa) {
  const rows = Array.isArray(aoa) ? aoa : [];
  const storeRow = rows[0] || [];        // Row 1 — store names
  const headerLen = Math.max(storeRow.length, (rows[1] || []).length);
  const blocks = storeBlockCols(headerLen)
    .map((c) => ({ col: c, rawStore: storeRow[c] }))
    .filter((b) => b.rawStore != null && String(b.rawStore).trim() !== '');   // skip blank store blocks

  const out = [];
  for (let r = 2; r < rows.length; r++) {
    const row = rows[r] || [];
    const rawWeek = row[0];
    if (rawWeek == null || String(rawWeek).trim() === '') continue;   // skip blank week rows
    const week_start = parseWeekStart(rawWeek);
    blocks.forEach((b) => {
      // Within a store's 5-col block: [bills, customer(IGNORE), Mobile, Email, %(IGNORE)]
      const bills   = num(row[b.col]);       // col + 0
      const mobiles = num(row[b.col + 2]);    // col + 2  (customer at +1 ignored)
      const emails  = num(row[b.col + 3]);    // col + 3
      // Skip a completely blank cell-group (this store had no data that week).
      if (bills == null && mobiles == null && emails == null) return;
      const resolvedStore = (typeof mapAmanahStore === 'function') ? mapAmanahStore(String(b.rawStore).trim()) : null;
      let save = true, reason = '', rollup = false;
      if (!resolvedStore) {
        save = false;
        rollup = isRollupHeader(b.rawStore);
        reason = rollup
          ? 'Roll-up column — totals are calculated automatically, nothing to save'
          : 'Store not recognised — won\'t be saved';
      } else if (!week_start) { save = false; reason = 'Week label couldn\'t be read — won\'t be saved'; }
      out.push({
        rawStore: String(b.rawStore).trim(),
        resolvedStore: resolvedStore || null,
        rawWeek: String(rawWeek).trim(),
        week_start: week_start || null,
        bills, mobiles, emails,
        save, reason, rollup,
      });
    });
  }
  return out;
}

// ── MODULE STATE + UPLOAD FLOW ────────────────────────────────────────────────
let _parsedRows = null;     // last parseSheet() result, awaiting commit
let _period = initialPeriod();

const PERIOD_LS_KEY = 'dataCapturePeriod';
function initialPeriod() {
  try {
    const v = (typeof localStorage !== 'undefined') ? localStorage.getItem(PERIOD_LS_KEY) : null;
    if (v && PERIODS.indexOf(v) !== -1) return v;
  } catch (e) { /* private mode */ }
  return 'month';
}

function api(path, opts) {
  opts = opts || {};
  const token = adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  if (opts.body) headers['Content-Type'] = 'application/json';
  return fetch(base + path, { cache: 'no-store', method: opts.method || 'GET', headers: headers, body: opts.body });
}

function root() { return document.getElementById('dataCaptureRoot'); }

// Drop zone / file picker wiring. Idempotent (safe to call from render()).
export function wireDropZone() {
  const dz = document.getElementById('dcDropZone');
  const fi = document.getElementById('dcFileInput');
  if (!dz || !fi) return;
  if (dz.dataset.dcWired) return;
  dz.dataset.dcWired = '1';
  dz.addEventListener('click', () => fi.click());
  fi.addEventListener('change', (e) => {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  });
  dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.style.background = 'var(--elc-green-soft)'; dz.style.borderColor = '#059669'; });
  dz.addEventListener('dragleave', () => { dz.style.background = 'var(--surface-2)'; dz.style.borderColor = 'var(--border-strong)'; });
  dz.addEventListener('drop', (e) => {
    e.preventDefault(); dz.style.background = 'var(--surface-2)'; dz.style.borderColor = 'var(--border-strong)';
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  });
}

async function handleFile(file) {
  const statusEl = document.getElementById('dcParseStatus');
  if (statusEl) { statusEl.style.color = 'var(--tiny)'; statusEl.textContent = '⏳ Parsing ' + file.name + '…'; }
  try {
    await ensureXlsx();   // v136.8: loads on first use; a failed load lands in the catch below with a plain message
    const ab = await file.arrayBuffer();
    const wb = window.XLSX.read(ab, { type: 'array' });
    // The capture sheet is a single transposed matrix — take the first sheet.
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet) throw new Error('The workbook has no sheets.');
    const aoa = window.XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });
    const parsed = parseSheet(aoa);
    _parsedRows = parsed;
    const saveable = parsed.filter((r) => r.save).length;
    // Count skipped ROLL-UP columns (distinct headers, e.g. the per-AM totals) —
    // benign by design — separately from genuine warnings, so a good upload
    // never reads as broken (v125.30, Matt's amended template).
    const rollupCols = new Set(parsed.filter((r) => !r.save && r.rollup).map((r) => r.rawStore)).size;
    const warned = parsed.filter((r) => !r.save && !r.rollup).length;
    if (statusEl) {
      statusEl.style.color = saveable > 0 ? 'var(--ap-teal-text)' : 'var(--ui-warn)';
      statusEl.textContent = '✓ Parsed ' + parsed.length + ' rows — ' + saveable + ' store rows ready to save' +
        (rollupCols ? ' · ' + rollupCols + ' roll-up column' + (rollupCols === 1 ? '' : 's') + ' skipped (totals are recalculated automatically)' : '') +
        (warned ? ' · ' + warned + ' flagged (won\'t be saved)' : '');
    }
    renderPreview();
  } catch (err) {
    console.error('[dataCapture] parse failed:', err);
    if (statusEl) { statusEl.style.color = 'var(--danger-deep)'; statusEl.textContent = '✗ Parse failed: ' + (err.message || err); }
    _parsedRows = null;
    const card = document.getElementById('dcPreviewCard');
    if (card) card.style.display = 'none';
  }
}

function renderPreview() {
  const card = document.getElementById('dcPreviewCard');
  const tableEl = document.getElementById('dcPreviewTable');
  if (!card || !tableEl || !_parsedRows) return;
  card.style.display = 'block';
  const saveable = _parsedRows.filter((r) => r.save);
  const warned = _parsedRows.filter((r) => !r.save && !r.rollup);
  const rollups = _parsedRows.filter((r) => !r.save && r.rollup);
  const rollupCols = new Set(rollups.map((r) => r.rawStore)).size;

  const summary = document.getElementById('dcPreviewSummary');
  if (summary) {
    summary.innerHTML = '<b>' + saveable.length + '</b> store row' + (saveable.length === 1 ? '' : 's') +
      ' will be saved' +
      (rollupCols ? ' · ' + rollupCols + ' roll-up column' + (rollupCols === 1 ? '' : 's') + ' skipped <span style="color:var(--sub)">(totals are recalculated automatically)</span>' : '') +
      (warned.length ? ' · <b style="color:var(--ui-warn)">' + warned.length + '</b> flagged (won\'t be saved)' : '');
  }
  const commitBtn = document.getElementById('dcCommitBtn');
  if (commitBtn) { commitBtn.disabled = saveable.length === 0; commitBtn.textContent = '✅ Save ' + saveable.length + ' row' + (saveable.length === 1 ? '' : 's'); }

  // One preview row per parsed cell-group. GENUINE warnings are amber; skipped
  // roll-up columns are neutral grey (deliberate, not a problem — v125.30).
  const rowHtml = (r) => {
    const bg = r.save ? 'var(--card)' : (r.rollup ? 'var(--surface-2)' : 'var(--ap-orange-bg)');
    const tone = r.rollup ? 'var(--sub)' : 'var(--ui-warn)';
    const storeCell = r.save
      ? '<span translate="no">' + esc(r.resolvedStore) + '</span>'
      : '<span translate="no" style="color:' + tone + '">' + esc(r.rawStore) + '</span>';
    const weekCell = r.week_start
      ? esc(r.week_start)
      : '<span translate="no" style="color:' + tone + '">' + esc(r.rawWeek) + '</span>';
    const flag = r.save ? '' : '<div style="font-size:11px;color:' + tone + ';margin-top:2px">' + (r.rollup ? 'ℹ' : '⚠') + ' ' + esc(r.reason) + '</div>';
    return '<tr style="border-bottom:1px solid var(--border);background:' + bg + '">' +
      '<td style="padding:6px 8px">' + storeCell + flag + '</td>' +
      '<td style="padding:6px 8px;font-variant-numeric:tabular-nums">' + weekCell + '</td>' +
      '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + (r.bills   == null ? '—' : r.bills) + '</td>' +
      '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + (r.mobiles == null ? '—' : r.mobiles) + '</td>' +
      '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + (r.emails  == null ? '—' : r.emails) + '</td>' +
      '</tr>';
  };
  // GENUINE warnings first (the admin must see real problems without scrolling),
  // then the rows that will save, then the benign roll-up skips LAST.
  const ordered = warned.concat(saveable, rollups);
  let html = '<table style="width:100%;border-collapse:collapse;font-size:12px">';
  html += '<thead><tr style="background:var(--ap-solid);color:#fff;position:sticky;top:0">';
  html += '<th style="padding:6px 8px;text-align:start">Store</th>';
  html += '<th style="padding:6px 8px;text-align:start">Week start</th>';
  html += '<th style="padding:6px 8px;text-align:end">Bills</th>';
  html += '<th style="padding:6px 8px;text-align:end">Mobiles</th>';
  html += '<th style="padding:6px 8px;text-align:end">Emails</th>';
  html += '</tr></thead><tbody>';
  ordered.forEach((r) => { html += rowHtml(r); });
  html += '</tbody></table>';
  tableEl.innerHTML = html;
}

export async function commitUpload() {
  if (!_parsedRows) return;
  const saveable = _parsedRows.filter((r) => r.save).map((r) => ({
    store: r.resolvedStore,
    week_start: r.week_start,
    bills: r.bills,
    mobiles: r.mobiles,
    emails: r.emails,
  }));
  if (saveable.length === 0) { toast('Nothing to save — every row is flagged.', 'error'); return; }
  const btn = document.getElementById('dcCommitBtn');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Saving…'; }
  const errBox = document.getElementById('dcCommitError');
  if (errBox) { errBox.style.display = 'none'; errBox.textContent = ''; }
  try {
    const res = await api('/api/admin/data-capture', { method: 'POST', body: JSON.stringify(saveable) });
    if (!res.ok) {
      const t = await res.text();
      throw new Error('HTTP ' + res.status + ': ' + t);
    }
    const result = await res.json();
    toast('Saved ' + (result.inserted != null ? result.inserted : saveable.length) + ' rows.', 'success');
    cancelUpload();
    renderGrid();          // refresh the KPI grid with the new data
  } catch (err) {
    console.error('[dataCapture] commit failed:', err);
    toast('Save failed: ' + (err.message || err), 'error');
    // v125.30: a toast vanishes — keep the REAL error on screen so it can be
    // read/screenshotted and sent to the dev instead of "it's not working".
    if (errBox) {
      errBox.style.display = 'block';
      errBox.textContent = '✗ Save failed — nothing was saved. Details: ' + String(err.message || err).slice(0, 600) +
        ' — please screenshot this line and send it to the developer.';
    }
    if (btn) { btn.disabled = false; btn.textContent = '✅ Save rows'; }
  }
}

export function cancelUpload() {
  _parsedRows = null;
  const card = document.getElementById('dcPreviewCard');
  if (card) card.style.display = 'none';
  const fi = document.getElementById('dcFileInput');
  if (fi) fi.value = '';
  const statusEl = document.getElementById('dcParseStatus');
  if (statusEl) statusEl.textContent = '';
}

// ── KPI GRID ──────────────────────────────────────────────────────────────────
export function setPeriod(p) {
  if (PERIODS.indexOf(p) === -1 || p === _period) return;
  _period = p;
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(PERIOD_LS_KEY, p); } catch (e) { /* ignore */ }
  renderGrid();
}

// Fetch window: enough history to fill the widest grid. A calendar-year period
// showing 5 years needs ~5 years back; everything else fits inside that.
function fromDateFor(period) {
  const d = new Date();
  const yearsBack = period === 'year' ? 6 : 2;   // generous — the roll-up caps columns anyway
  d.setFullYear(d.getFullYear() - yearsBack);
  return d.toISOString().slice(0, 10);
}

function periodToggle() {
  const btn = (p, label) => {
    const on = p === _period;
    const radius = p === 'week' ? ';border-radius:8px 0 0 8px'
      : p === 'year' ? ';border-radius:0 8px 8px 0;border-left:none'
      : ';border-left:none';
    return '<button onclick="DataCapture.setPeriod(\'' + p + '\')" ' +
      'style="border:1px solid var(--border-strong);background:' + (on ? 'var(--ap-solid)' : 'var(--card)') + ';color:' + (on ? '#fff' : 'var(--text)') +
      ';padding:6px 14px;font-size:13px;font-weight:700;cursor:pointer' + radius + '">' + esc(label) + '</button>';
  };
  return '<div style="display:inline-flex;margin-bottom:14px">' +
    btn('week', 'Week') + btn('month', 'Month') + btn('quarter', 'Quarter') + btn('year', 'Year') + '</div>';
}

// One cell renders BOTH metrics stacked: mobile% (top) and email% (bottom), each
// traffic-lit by its own default target. Never combined.
function metricCell(cell) {
  if (!cell || cell.bills === 0 || cell.bills == null) {
    return '<td style="text-align:center;padding:8px 10px;color:var(--tiny);border-bottom:1px solid var(--border)">–</td>';
  }
  const mBand = bandStyle(captureBand('mobile', cell.mobilePct));
  const eBand = bandStyle(captureBand('email', cell.emailPct));
  const line = (label, pct, st) =>
    '<div style="display:flex;align-items:center;justify-content:center;gap:4px">' +
      '<span style="font-size:9px;font-weight:700;color:var(--tiny);width:14px;text-align:end">' + label + '</span>' +
      '<span style="font-size:13px;font-weight:800;color:' + st.fg + ';background:' + st.bg +
        ';border-radius:4px;padding:1px 6px;min-width:44px;display:inline-block">' +
        (pct == null ? '—' : pct.toFixed(1) + '%') + '</span>' +
    '</div>';
  return '<td style="text-align:center;padding:6px 8px;border-bottom:1px solid var(--border)">' +
    line('M', cell.mobilePct, mBand) +
    '<div style="height:3px"></div>' +
    line('E', cell.emailPct, eBand) +
    '<div style="font-size:9px;color:var(--tiny);margin-top:2px">' + (cell.bills || 0) + ' bills</div>' +
    '</td>';
}

function grid(rows, allRow, cols) {
  // A store name stays as written; the bold 'All stores' row is a label and is translated.
  const nameCell = (label, bold) =>
    '<td' + (bold ? '' : ' translate="no"') + ' style="text-align:start;padding:8px 12px;border-bottom:1px solid var(--border);position:sticky;inset-inline-start:0;background:var(--card);' +
      (bold ? 'font-weight:800' : 'font-weight:600') + ';white-space:nowrap;min-width:150px">' + esc(label) + '</td>';
  const header = '<tr><th style="text-align:start;padding:8px 12px;position:sticky;inset-inline-start:0;background:var(--surface-2);font-size:11px;' +
      'text-transform:uppercase;letter-spacing:.4px;color:var(--sub);border-bottom:1px solid var(--border);min-width:150px">Store</th>' +
    cols.map((c) => '<th style="text-align:center;padding:8px 10px;font-size:11px;font-weight:800;color:var(--text);' +
      'border-bottom:1px solid var(--border);background:var(--surface-2);min-width:78px">' + esc(c.colLabel) + '</th>').join('') + '</tr>';
  const allTr = '<tr style="background:var(--surface-2)">' + nameCell(allRow.store, true) + allRow.cells.map(metricCell).join('') + '</tr>';
  const storeTrs = rows.map((r) => '<tr>' + nameCell(r.store, false) + r.cells.map(metricCell).join('') + '</tr>').join('');
  return '<div style="overflow-x:auto;-webkit-overflow-scrolling:touch;border:1px solid var(--border);border-radius:12px">' +
    '<table style="border-collapse:collapse;width:100%;background:var(--card)">' +
    '<thead>' + header + '</thead><tbody>' + allTr + storeTrs + '</tbody></table></div>';
}

function legend() {
  const t = RAG_TARGETS;
  return '<div style="font-size:11px;color:var(--tiny);margin-top:8px;line-height:1.6">' +
    '<b>M</b> = mobile capture % (Σmobiles ÷ Σbills) · <b>E</b> = email capture % (Σemails ÷ Σbills), summed over the period then divided.<br>' +
    'Mobile: green ≥ ' + t.mobile.green + '% · amber ≥ ' + t.mobile.amber + '% · red below. ' +
    'Email: green ≥ ' + t.email.green + '% · amber ≥ ' + t.email.amber + '% · red below. ' +
    '<span style="color:var(--tiny)">(default targets — editable targets come in a later update.)</span> Worst mobile% store on top.' +
    '</div>';
}

export async function renderGrid() {
  const pane = document.getElementById('dcGridRoot');
  if (!pane) return;
  pane.innerHTML = periodToggle() + '<div style="padding:16px;color:var(--tiny)">Loading capture data…</div>';
  let raw;
  try {
    const from = fromDateFor(_period);
    const res = await api('/api/admin/data-capture?from=' + encodeURIComponent(from));
    if (res.status === 401 || res.status === 403) {
      pane.innerHTML = periodToggle() + '<div style="padding:16px;color:var(--danger-deep)">Your session looks expired — sign out and back in.</div>';
      return;
    }
    if (!res.ok) throw new Error('status ' + res.status);
    raw = await res.json();
  } catch (e) {
    pane.innerHTML = periodToggle() + '<div style="padding:16px;color:var(--danger-deep)">Could not load capture data (' + esc((e && e.message) || e) + ').</div>';
    return;
  }
  const rowsRaw = Array.isArray(raw) ? raw : [];
  if (!rowsRaw.length) {
    pane.innerHTML = periodToggle() +
      '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">No capture data yet. Upload a weekly sheet above to get started.</div>';
    return;
  }
  const { cols, allRow, rows } = rollup(rowsRaw, _period, colCap(_period));
  if (!cols.length) {
    pane.innerHTML = periodToggle() +
      '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">No capture data in this period.</div>';
    return;
  }
  pane.innerHTML = periodToggle() + grid(rows, allRow, cols) + legend();
}

// ── TAB SHELL ─────────────────────────────────────────────────────────────────
function ensureShell() {
  const el = root();
  if (!el) return null;
  if (!document.getElementById('dcGridRoot')) {
    el.innerHTML =
      '<div style="margin-bottom:14px"><div style="font-size:18px;font-weight:800">📱 Customer Data Capture</div>' +
        '<div style="font-size:12px;color:var(--tiny)">Weekly mobile &amp; email capture per store. Upload the weekly sheet, then review the KPI grid.</div></div>' +
      // Upload card
      '<div style="border:1px solid var(--border);border-radius:12px;padding:14px;background:var(--surface-2);margin-bottom:18px">' +
        '<div style="font-size:13px;font-weight:700;margin-bottom:8px">Upload weekly sheet</div>' +
        '<div id="dcDropZone" style="border:2px dashed var(--border-strong);border-radius:10px;padding:22px;text-align:center;cursor:pointer;background:var(--surface-2)">' +
          '<div style="font-size:14px;font-weight:600;color:var(--text)">Drop the capture spreadsheet here, or click to choose</div>' +
          '<div style="font-size:12px;color:var(--tiny);margin-top:4px">.xlsx / .xls — the transposed weekly matrix</div>' +
        '</div>' +
        '<input id="dcFileInput" type="file" accept=".xlsx,.xls" style="display:none">' +
        '<div id="dcParseStatus" style="font-size:13px;margin-top:10px"></div>' +
        // Preview (hidden until a file is parsed)
        '<div id="dcPreviewCard" style="display:none;margin-top:14px">' +
          '<div id="dcPreviewSummary" style="font-size:13px;margin-bottom:8px"></div>' +
          '<div style="max-height:340px;overflow:auto;border:1px solid var(--border);border-radius:8px"><div id="dcPreviewTable"></div></div>' +
          '<div style="margin-top:12px;display:flex;gap:8px">' +
            '<button id="dcCommitBtn" onclick="DataCapture.commitUpload()" style="background:#059669;color:#fff;border:none;border-radius:8px;padding:9px 16px;font-weight:700;cursor:pointer">✅ Save rows</button>' +
            '<button onclick="DataCapture.cancelUpload()" style="background:var(--card);border:1px solid var(--border-strong);border-radius:8px;padding:9px 16px;cursor:pointer">Cancel</button>' +
          '</div>' +
          // v125.30: a save failure stays ON SCREEN (a toast vanishes) so the
          // real error can be screenshotted instead of reported as "not working".
          '<div id="dcCommitError" style="display:none;margin-top:10px;padding:10px 12px;background:var(--danger-bg);border:1px solid var(--ap-danger-line);border-radius:8px;color:var(--danger-deep);font-size:12.5px;white-space:pre-wrap"></div>' +
        '</div>' +
      '</div>' +
      // KPI grid
      '<div style="font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--tiny);margin:6px 0 10px">Capture KPI</div>' +
      '<div id="dcGridRoot"></div>';
  }
  return el;
}

export function render() {
  if (!ensureShell()) return;
  wireDropZone();
  renderGrid();
}

// ── window binding (frontend-breakup rule: moved functions are window-bound) ──
if (typeof window !== 'undefined') {
  window.DataCapture = {
    render, renderGrid, setPeriod,
    wireDropZone, commitUpload, cancelUpload,
    // pure helpers exposed for reuse/debugging
    rollup, parseSheet, parseWeekStart,
  };
}
